// Public read-only scenario demo at /demo/:athlete/:day — no login required.
// Useful for cold outreach: text a link to an athletic director and they land
// on a fully-rendered "live" portal scoped to that exact engine state.
import { useParams, Link, Navigate } from "react-router-dom";
import { ARCHETYPES } from "@/components/ScenarioPicker";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import HealthExplainer from "@/components/HealthExplainer";

const BANDS = { A: "#D90429", B: "#F59E0B", C: "#3B82F6", D: "#22D3EE", E: "#16A34A" };

export default function PublicDemo() {
  const { athleteId, dayId } = useParams();
  const athlete = ARCHETYPES.find((a) => a.id === athleteId);
  if (!athlete) return <Navigate to="/" replace />;
  const scenario = athlete.scenarios.find((s) => s.id === dayId) || athlete.scenarios[0];
  const c = BANDS[scenario.hybrid] || "#22D3EE";
  const band = scenario.score >= 80 ? "Peak" : scenario.score >= 65 ? "Good" : scenario.score >= 50 ? "Caution" : "Risk";

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: "var(--ink-1)" }} data-testid="public-demo">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--brand-secondary)" }}>
            <Sparkles className="h-3 w-3" /> Live Demo · armiqapp.com
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 pt-6 space-y-5">
        <div className="rounded-xl p-4"
             style={{ backgroundColor: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.35)" }}>
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
            Demo · {athlete.name}
          </div>
          <div className="font-display font-bold text-white text-base mt-0.5">{scenario.title}</div>
          <div className="text-[10px] text-white/40 mt-0.5 italic">{athlete.headline}</div>
          <p className="text-sm text-white/70 mt-3">{scenario.note}</p>

          <div className="mt-3 grid grid-cols-4 gap-2 text-center">
            <Stat label="Score" value={scenario.score} />
            <Stat label="Hybrid" value={scenario.hybrid} color={c} />
            <Stat label="Pitches" value={scenario.pitch_count} />
            <Stat label="Recovery" value={`${scenario.sub.recovery}%`} />
          </div>
        </div>

        {/* Mock "next track" card — same visual as the live portal */}
        <div className="rounded-2xl p-6 text-white border" style={{ backgroundColor: "var(--ink-2)", borderColor: "var(--ink-border-strong)" }}>
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
            Your Next Track
          </div>
          <div className="mt-2 font-display font-black text-4xl md:text-5xl tracking-tighter">
            Hybrid {scenario.hybrid}
          </div>
          <div className="text-white/70 text-sm mt-1 uppercase tracking-[0.18em] font-semibold">
            {scenario.hybrid === "A" ? "Game day" : scenario.hybrid === "B" ? "Build" : scenario.hybrid === "C" ? "Short long toss" : scenario.hybrid === "D" ? "Light recovery" : "Full reset"}
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-lg p-3"
               style={{ backgroundColor: `${c}1f`, border: `1px solid ${c}55` }}>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full font-display font-black text-lg"
                  style={{ backgroundColor: c, color: "#fff" }}>{scenario.hybrid}</span>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: c }}>Today's Suggested Hybrid</div>
              <div className="text-sm text-white/80">Readiness <span className="font-mono-num font-bold text-white">{scenario.score}</span> · band <span className="font-bold text-white">{band}</span></div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-4 gap-2 text-center text-xs">
            {Object.entries(scenario.sub).map(([k, v]) => (
              <div key={k} className="rounded border border-white/10 p-2">
                <div className="text-[9px] tracking-[0.18em] uppercase text-white/40 capitalize">{k}</div>
                <div className="font-display font-black text-base mt-0.5 text-white">{v}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Other scenarios for this athlete */}
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">
            Other moments in {athlete.name.split(" ")[0]}'s week
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {athlete.scenarios.map((s) => {
              const active = s.id === scenario.id;
              const sc = BANDS[s.hybrid];
              return (
                <Link
                  key={s.id}
                  to={`/demo/${athlete.id}/${s.id}`}
                  data-testid={`public-day-${s.id}`}
                  className="shrink-0 rounded px-2 py-1 text-[10px] font-extrabold tracking-[0.12em] uppercase"
                  style={{
                    backgroundColor: active ? `${sc}22` : "transparent",
                    border: `1px solid ${active ? sc : "rgba(255,255,255,0.15)"}`,
                    color: active ? "white" : "rgba(255,255,255,0.55)",
                  }}
                >
                  {s.label} → <span style={{ color: sc }}>{s.hybrid}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Other athletes */}
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">
            See another archetype
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {ARCHETYPES.filter((a) => a.id !== athlete.id).map((a) => (
              <Link
                key={a.id}
                to={`/demo/${a.id}/${a.scenarios[0].id}`}
                className="rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
                data-testid={`public-athlete-${a.id}`}
              >
                <div className="font-semibold text-white text-sm">{a.name}</div>
                <div className="text-[10px] text-white/50 mt-0.5 leading-tight">{a.headline}</div>
              </Link>
            ))}
          </div>
        </div>

        <HealthExplainer />

        <div className="rounded-xl p-6 text-center"
             style={{ backgroundColor: "var(--ink-2)", border: "1px solid var(--ink-border-strong)" }}>
          <h2 className="font-display font-bold text-2xl text-white">This is one scenario. Your athletes get this every day.</h2>
          <p className="text-sm text-white/65 mt-2">ArmIQ turns daily biometrics + self-logs into one number, one prescribed hybrid, one decision your staff doesn't have to second-guess.</p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/pricing"><Button data-testid="public-pricing-btn">See pricing <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
            <Link to="/login"><Button variant="outline" data-testid="public-login-btn">Coach sign-in <ExternalLink className="h-3 w-3 ml-1.5" /></Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded border border-white/10 bg-white/5 p-2">
      <div className="text-[9px] tracking-[0.2em] uppercase text-white/40">{label}</div>
      <div className="font-display font-black text-xl mt-0.5" style={{ color: color || "white" }}>{value}</div>
    </div>
  );
}
